import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CompanyDetailsSidebar } from '@/components/analytics/company-details-sidebar';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCollectionCasesListGraphQL } from '@/hooks/useCollectionCasesGraphQL';
import { useCompanyRecentActivities } from '@/hooks/useCompanyRecentActivitiesGraphQL';
import { useCustomersListGraphQL } from '@/hooks/useCustomersGraphQL';
import { useDisputesListGraphQL } from '@/hooks/useDisputesGraphQL';
import { useMultiCompanyDetailGraphQL } from '@/hooks/useMultiCompanyDetailGraphQL';
import { ACTIVITY_TYPE_TO_TYPE } from '@/lib/activity-type-maps';
import { cn, formatDate } from '@/lib/utils';

interface PriorityItem {
  id: string;
  type: 'case' | 'dispute';
  customerName: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  dueDate: string;
  dueDateRaw: number;
}

// Activity icon/colour helpers
const activityConfig: Record<string, { icon: React.ElementType; color: string }> = {
  payment: { icon: CheckCircle2, color: 'text-green-600' },
  promise: { icon: Clock, color: 'text-blue-600' },
  email: { icon: Mail, color: 'text-purple-600' },
  phone: { icon: Phone, color: 'text-teal-600' },
  dispute: { icon: MessageSquare, color: 'text-orange-600' },
  case: { icon: AlertCircle, color: 'text-red-600' },
  note: { icon: FileText, color: 'text-gray-600' },
  system: { icon: Clock, color: 'text-gray-500' },
};

function getActivityConfig(type: string) {
  return activityConfig[type] ?? { icon: Clock, color: 'text-gray-500' };
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export default function CompanyDetailsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const numericCompanyId = Number(companyId) || 0;
  const { data: detailData, isLoading } = useMultiCompanyDetailGraphQL(numericCompanyId);

  const company = detailData?.company ?? null;
  const trendData = detailData?.trendPoints ?? [];

  // Top customers from live API (sorted by outstanding desc, top 10)
  const { data: customersData, isLoading: isCustomersLoading } = useCustomersListGraphQL({
    pageSize: 10,
    sortBy: 'OutstandingHome',
    sortOrder: 'desc',
  });
  const topCustomers = customersData?.customers ?? [];

  // Today's priorities: merge collection cases, disputes, and tasks
  const { data: casesData, isLoading: isCasesLoading } = useCollectionCasesListGraphQL({
    pageSize: 8,
    sortBy: 'nextActionDate',
    sortOrder: 'asc',
  });
  const { data: disputesData, isLoading: isDisputesLoading } = useDisputesListGraphQL({
    pageSize: 8,
    sortBy: 'dueDate',
    sortOrder: 'asc',
    statuses: ['New', 'In Progress', 'Under Review', 'Escalated'],
  });
  const isPrioritiesLoading = isCasesLoading || isDisputesLoading;

  const todaysPriorities = useMemo<PriorityItem[]>(() => {
    const items: PriorityItem[] = [];

    for (const c of casesData?.cases ?? []) {
      const raw = c.nextActionDate ? new Date(c.nextActionDate).getTime() : Number.MAX_SAFE_INTEGER;
      items.push({
        id: `case-${c.id}`,
        type: 'case',
        customerName: c.customerName,
        description: c.nextAction,
        priority: c.priority.toLowerCase() as 'high' | 'medium' | 'low',
        dueDate: c.nextActionDate ? formatDate(c.nextActionDate.toISOString()) : '—',
        dueDateRaw: raw,
      });
    }

    for (const d of disputesData?.disputes ?? []) {
      const raw = d.dueDate ? new Date(d.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      items.push({
        id: `dispute-${d.id}`,
        type: 'dispute',
        customerName: d.customer,
        description: d.description || d.type || 'Dispute',
        priority: (d.priority || 'medium').toLowerCase() as 'high' | 'medium' | 'low',
        dueDate: d.dueDate ? formatDate(d.dueDate) : '—',
        dueDateRaw: raw,
      });
    }

    items.sort((a, b) => a.dueDateRaw - b.dueDateRaw);
    return items.slice(0, 8);
  }, [casesData, disputesData]);

  // Recent activity from live API (all activities for the company)
  const { activities: recentActivityData, isLoading: isActivitiesLoading } = useCompanyRecentActivities();
  const recentActivity = useMemo(
    () =>
      recentActivityData.slice(0, 10).map((a) => ({
        id: a.id,
        type: ACTIVITY_TYPE_TO_TYPE[a.activityType] ?? a.activityType,
        title: a.title,
        customerName: a.customerName,
        date: a.createdAt,
      })),
    [recentActivityData],
  );

  const formatCurrency = (value: number, currency: string) => {
    const locale = currency === 'USD' ? 'en-US' : 'en-GB';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground text-lg">Company not found.</p>
          <Button onClick={() => navigate('/analytics-reporting/multi-company')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar */}
      <div
        className={cn('relative transition-all duration-300 ease-in-out shrink-0', isSidebarCollapsed ? 'w-0' : 'w-80')}
      >
        <CompanyDetailsSidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(true)} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Collapsed sidebar toggle handle */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Companies"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="[writing-mode:vertical-lr] text-xs font-medium tracking-wider rotate-180">
                  COMPANIES
                </div>
              </div>
            </div>
          </button>
        )}

        <div className="flex flex-col h-full overflow-hidden">
          {/* Page Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-shrink-0">
            <div className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold tracking-tight">{company.name}</h1>
                    <p className="text-muted-foreground text-sm">Company performance overview and analytics</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => navigate('/analytics-reporting/multi-company')}>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StaticMetricCardAlt
                  title="TOTAL OUTSTANDING"
                  value={formatCurrency(company.totalAR, company.currency)}
                  change=""
                  changeLabel="Total accounts receivable"
                  icon={Banknote}
                  variant="info"
                />
                <StaticMetricCardAlt
                  title="TOTAL OVERDUE"
                  value={formatCurrency(company.overdue, company.currency)}
                  change={`${company.overduePercent.toFixed(1)}%`}
                  changeLabel="of total AR"
                  icon={TrendingUp}
                  variant="error"
                />
                <StaticMetricCardAlt
                  title="DSO"
                  value={`${company.dso} days`}
                  change=""
                  changeLabel="Days sales outstanding"
                  icon={Calendar}
                  variant="warning"
                />
                <StaticMetricCardAlt
                  title="CEI"
                  value={`${company.cei}%`}
                  change=""
                  changeLabel="Collection effectiveness"
                  icon={CheckCircle2}
                  variant="success"
                />
              </div>

              {/* Additional Stats Row */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Avg Days to Pay</p>
                  <p className="text-2xl font-bold mt-1">{company.avgDaysToPay} days</p>
                  <p className="text-xs text-muted-foreground mt-1">Average payment speed</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Customer Count</p>
                  <p className="text-2xl font-bold mt-1">{company.customerCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">Active customers</p>
                </Card>
              </div>

              {/* 30-Day Performance Trend */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">30-Day Performance Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="dso"
                      stroke="#00897B"
                      strokeWidth={2}
                      name="DSO"
                      dot={{ fill: '#00897B', r: 4 }}
                      animationDuration={1500}
                    />
                    <Line
                      type="monotone"
                      dataKey="cei"
                      stroke="#2196F3"
                      strokeWidth={2}
                      name="CEI"
                      dot={{ fill: '#2196F3', r: 4 }}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Today's Priorities & Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Today's Priorities */}
                <Card className="p-6 flex flex-col">
                  <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Today's Priorities</h3>
                  <div className="overflow-y-auto max-h-64 space-y-3 pr-1">
                    {isPrioritiesLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={`priority-skeleton-${i}`} className="h-16" />
                      ))
                    ) : todaysPriorities.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No active priorities</p>
                    ) : (
                      todaysPriorities.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => navigate('/customers')}
                          className={cn(
                            'w-full text-left p-4 border-l-4 bg-muted/30 rounded-r-lg hover:bg-muted/50 transition-colors',
                            item.type === 'case' && 'border-l-teal-500',
                            item.type === 'dispute' && 'border-l-orange-500',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-sm text-gray-900 truncate">{item.customerName}</p>
                                <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 capitalize">
                                  {item.type}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-0.5 truncate">{item.description}</p>
                            </div>
                            <Badge
                              variant={item.priority === 'high' ? 'destructive' : 'secondary'}
                              className="shrink-0 capitalize text-xs"
                            >
                              {item.priority}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Due: {item.dueDate}</p>
                        </button>
                      ))
                    )}
                  </div>
                </Card>

                {/* Recent Activity */}
                <Card className="p-6 flex flex-col">
                  <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Recent Activity</h3>
                  <div className="overflow-y-auto max-h-64 space-y-4 pr-1">
                    {isActivitiesLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={`activity-skeleton-${i}`} className="h-12" />
                      ))
                    ) : recentActivity.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No recent activity</p>
                    ) : (
                      recentActivity.map((activity) => {
                        const { icon: Icon, color } = getActivityConfig(activity.type);
                        return (
                          <button
                            key={activity.id}
                            type="button"
                            onClick={() => navigate('/customers')}
                            className="w-full text-left flex items-start gap-3 hover:bg-muted/40 rounded-lg p-1 -mx-1 transition-colors"
                          >
                            <div className={cn('p-2 rounded-full bg-muted shrink-0', color)}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{activity.title}</p>
                              <p className="text-xs text-muted-foreground truncate">{activity.customerName}</p>
                            </div>
                            <p className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                              {timeAgo(activity.date)}
                            </p>
                          </button>
                        );
                      })
                    )}
                  </div>
                </Card>
              </div>

              {/* Top Customers by Outstanding */}
              <Card className="p-6 flex flex-col">
                <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Top Customers by Outstanding</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-y-auto max-h-64">
                    {isCustomersLoading && (
                      <div className="p-4 space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <Skeleton key={`customer-skeleton-${i}`} className="h-10" />
                        ))}
                      </div>
                    )}
                    {!isCustomersLoading && topCustomers.length === 0 && (
                      <p className="text-sm text-muted-foreground py-8 text-center">No customers found</p>
                    )}
                    {!isCustomersLoading && topCustomers.length > 0 && (
                      <table className="w-full">
                        <thead className="bg-muted sticky top-0 z-10">
                          <tr>
                            <th className="text-left p-3 text-sm font-medium">Customer</th>
                            <th className="text-right p-3 text-sm font-medium">Outstanding</th>
                            <th className="text-right p-3 text-sm font-medium">Overdue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topCustomers.map((customer) => {
                            const overdueDisplay =
                              customer.overdueHome > 0 ? (
                                formatCurrency(customer.overdueHome, company.currency)
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              );

                            return (
                              <tr key={customer.id} className="border-t hover:bg-muted/50 transition-colors">
                                <td className="p-3 text-sm font-medium">
                                  <button
                                    onClick={() => navigate(`/customers?search=${encodeURIComponent(customer.name)}`)}
                                    className="text-primary hover:underline text-left"
                                  >
                                    {customer.name}
                                  </button>
                                </td>
                                <td className="text-right p-3 text-sm font-medium">
                                  {formatCurrency(customer.outstandingHome, company.currency)}
                                </td>
                                <td className="text-right p-3 text-sm text-red-600 font-medium">{overdueDisplay}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
